/**
 * Cinematic Studio — a designated playground for building promo shots.
 *
 * Renders the real location scene (buildings, hangars, obstacles) but with
 * `renderAllocations={false}`, so NO live allocation appears and the studio owns
 * every aircraft in the scene. Actors are placed freely, optionally animated
 * along waypoint paths with autorotation, and the camera is flown by the shared
 * flythrough module. Scenes persist to localStorage and export/import as JSON.
 *
 * Nothing here writes to the database.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { ArrowLeft, FileStack, MousePointer2, Plane, Route } from 'lucide-react'
import { MapboxScene } from '@/components/3d'
import type { MapboxCustomLayerService } from '@/services/3d'
import { useAppContext, useMapBuildings, useInitialViewSettings } from '@/hooks'
import { useMapStore } from '@/stores/mapStore'
import { useCinemaStore } from '@/stores/cinemaStore'
import { CinemaToggleButton } from '@/components/cinema'
import { ToolbarButton, ToolbarDivider } from '@/components/overlay-ui'
import {
  FlythroughButton,
  FlythroughPanel,
  useFlythroughPlayback,
  useFlythroughStore,
} from '@/features/flythrough'
import { loadStudioCatalog } from './data/loadStudioCatalog'
import { useStudioStore, selectActiveScene, selectSelectedActor } from './store/studioStore'
import { useStudioSceneSync } from './hooks/useStudioSceneSync'
import { useStudioActorMotion } from './hooks/useStudioActorMotion'
import { useStudioPlayback } from './hooks/useStudioPlayback'
import { useStudioInteraction } from './hooks/useStudioInteraction'
import { AircraftLibraryPanel } from './components/AircraftLibraryPanel'
import { ActorInspectorPanel } from './components/ActorInspectorPanel'
import { SceneManagerPanel } from './components/SceneManagerPanel'
import { StudioTimelineBar } from './components/StudioTimelineBar'
import { PathOverlay } from './components/PathOverlay'
import { DEFAULT_SCENE_DURATION_MS } from './types'
import { runCinematicSelfChecks } from './selfCheck'

/** Which left-hand drawer is open (mutually exclusive). */
type LeftPanel = 'none' | 'scenes' | 'library'

/** Module-level so the checks run once per session, not once per mount. */
let selfChecksRun = false

export function CinematicStudioPage() {
  const { locationId, locationName, locationSlug } = useAppContext()
  // `useAppContext` yields `string | null`; the map hooks take `string | undefined`.
  const mapLocationId = locationId ?? undefined
  const { viewSettings: locationViewSettings, center } = useMapBuildings(mapLocationId)
  const initialViewSettings = useInitialViewSettings(mapLocationId, locationViewSettings)

  const [layerService, setLayerService] = useState<MapboxCustomLayerService | null>(null)
  const [mapInstance, setMapInstance] = useState<mapboxgl.Map | null>(null)
  const [sceneReady, setSceneReady] = useState(false)
  const [leftPanel, setLeftPanel] = useState<LeftPanel>('scenes')
  const [inspectorOpen, setInspectorOpen] = useState(true)
  const [flythroughOpen, setFlythroughOpen] = useState(false)

  const cinemaActive = useCinemaStore((s) => s.active)

  // ── Store ─────────────────────────────────────────────────────────────────
  const scene = useStudioStore(selectActiveScene)
  const selectedActor = useStudioStore(selectSelectedActor)
  const selectedWaypointId = useStudioStore((s) => s.selectedWaypointId)
  const tool = useStudioStore((s) => s.tool)
  const setTool = useStudioStore((s) => s.setTool)
  const currentMs = useStudioStore((s) => s.currentMs)
  const createScene = useStudioStore((s) => s.createScene)
  const bindSceneToLocation = useStudioStore((s) => s.bindSceneToLocation)

  const actors = useMemo(() => scene?.aircraft ?? [], [scene])
  const durationMs = scene?.settings.durationMs ?? DEFAULT_SCENE_DURATION_MS
  const loopScene = scene?.settings.loopScene ?? true

  // Camera tours belong to the scene, so scope them by scene id.
  const flythroughScope = `cinematic:${scene?.meta.id ?? 'none'}`

  // Dev-only guard on the motion math — autorotation and the compass/scene
  // rotation conversion are the easiest things here to silently break.
  useEffect(() => {
    if (!import.meta.env.DEV || selfChecksRun) return
    selfChecksRun = true
    const results = runCinematicSelfChecks()
    const failed = results.filter((r) => !r.pass)
    if (failed.length) console.error('[cinematic] self-checks FAILED', failed)
    else console.info('[cinematic] self-checks passed', results.length)
  }, [])

  // ── Catalog ───────────────────────────────────────────────────────────────
  const catalogQuery = useQuery({
    queryKey: ['cinematic-studio', 'catalog'],
    queryFn: loadStudioCatalog,
    staleTime: Infinity,
  })

  // ── Ensure there is always a scene to work in ────────────────────────────
  useEffect(() => {
    if (scene || !center) return
    createScene('Scene 1', locationId ?? null, locationSlug ?? null, center)
  }, [scene, center, createScene, locationId, locationSlug])

  // Re-stamp a scene opened at a different airfield so its origin matches what
  // we're actually rendering (positions are origin-relative, so the layout
  // transfers — it just needs the right anchor).
  useEffect(() => {
    if (!scene || !center) return
    if (scene.meta.locationId === (locationId ?? null) && scene.meta.origin) return
    bindSceneToLocation(locationId ?? null, locationSlug ?? null, center)
  }, [scene, center, locationId, locationSlug, bindSceneToLocation])

  // Hand the scene's saved camera tours to the flythrough module when the scene
  // changes, so tours travel with the document rather than the browser.
  const hydratedSceneRef = useRef<string | null>(null)
  useEffect(() => {
    if (!scene) return
    if (hydratedSceneRef.current === scene.meta.id) return
    hydratedSceneRef.current = scene.meta.id
    const existing = useFlythroughStore.getState().getScope(flythroughScope)
    if (existing.playlists.length === 0 && scene.playlists.length > 0) {
      useFlythroughStore.getState().loadScope(flythroughScope, scene.playlists)
    }
  }, [scene, flythroughScope])

  // ── Look settings drive the shared map store ─────────────────────────────
  useEffect(() => {
    if (!scene) return
    useMapStore.getState().updateMapboxConfig({ lightPreset: scene.settings.lightPreset })
  }, [scene?.settings.lightPreset, scene])

  useEffect(() => {
    if (!layerService || !scene) return
    layerService.setRoofVisibility(scene.settings.showRoofs)
  }, [layerService, scene?.settings.showRoofs, scene])

  // ── Scene <-> 3D ─────────────────────────────────────────────────────────
  const currentMsRef = useRef(0)
  currentMsRef.current = currentMs

  useStudioSceneSync(layerService, actors, catalogQuery.data, currentMsRef)
  const applyAt = useStudioActorMotion(layerService, actors)
  useStudioPlayback(applyAt, durationMs, loopScene, actors)

  useStudioInteraction({
    map: mapInstance,
    center: center ?? null,
    catalog: catalogQuery.data,
    // Authoring gestures would fight the camera during a flythrough, and are
    // meaningless while the UI is hidden for a capture.
    enabled: sceneReady && !cinemaActive,
  })

  useFlythroughPlayback({ map: mapInstance, layerService, scope: flythroughScope })

  // Pin aircraft detail: a promo stage should never show the low-LOD grey boxes.
  useEffect(() => {
    if (!layerService) return
    layerService.setLODEnabled(false)
    return () => layerService.setLODEnabled(true)
  }, [layerService])

  const handleSceneReady = useCallback(
    (refs: { map: mapboxgl.Map | null; layerService: MapboxCustomLayerService | null }) => {
      setLayerService(refs.layerService)
      setMapInstance(refs.map)
      if (refs.layerService) setSceneReady(true)
    },
    []
  )

  const toolButton = (id: typeof tool, icon: React.ReactNode, label: string, title: string) => (
    <ToolbarButton
      icon={icon}
      label={label}
      accent="sky"
      active={tool === id}
      onClick={() => setTool(id)}
      title={title}
    />
  )

  return (
    <div className="fixed inset-0 z-50 overflow-hidden bg-slate-950">
      <MapboxScene
        className="h-full w-full"
        locationId={mapLocationId}
        initialViewSettings={initialViewSettings}
        enableShadows
        interactionEnabled={false}
        renderAllocations={false}
        onReady={handleSceneReady}
        renderOverlay={(map) => (
          <div className="pointer-events-none absolute inset-0">
            {/* Motion path of the selected actor */}
            <PathOverlay
              map={map}
              center={center ?? null}
              actor={selectedActor}
              selectedWaypointId={selectedWaypointId}
              visible={!!selectedActor?.path}
            />

            {/* Top-left: back + title */}
            <div className="absolute left-4 top-4 flex items-center gap-3">
              <button
                type="button"
                onClick={() => window.history.back()}
                className="pointer-events-auto flex h-9 w-9 items-center justify-center rounded-lg
                           border border-white/10 bg-black/60 text-white/70 backdrop-blur-sm
                           transition-colors hover:text-white"
                title="Back"
              >
                <ArrowLeft className="h-4 w-4" />
              </button>
              <div className="rounded-lg border border-white/10 bg-black/60 px-3 py-1.5 backdrop-blur-sm">
                <div className="text-[10px] font-semibold uppercase tracking-widest text-white/30">
                  Cinematic Studio
                </div>
                <div className="text-sm font-bold text-white">
                  {scene?.meta.name ?? 'Untitled'}
                  <span className="ml-2 font-normal text-white/35">
                    {locationName ?? locationSlug}
                  </span>
                </div>
              </div>
            </div>

            {/* Top-right toolbar */}
            <div className="absolute right-4 top-4 flex items-center gap-1.5">
              <ToolbarButton
                icon={<FileStack className="h-4 w-4" />}
                label="Scenes"
                accent="emerald"
                active={leftPanel === 'scenes'}
                onClick={() => setLeftPanel((p) => (p === 'scenes' ? 'none' : 'scenes'))}
                title="Scenes — new, rename, import/export, look and timing"
              />
              <ToolbarButton
                icon={<Plane className="h-4 w-4" />}
                label="Aircraft"
                accent="sky"
                active={leftPanel === 'library'}
                onClick={() => setLeftPanel((p) => (p === 'library' ? 'none' : 'library'))}
                title="Aircraft library — pick a type to place"
              />
              <ToolbarDivider />
              {toolButton(
                'select',
                <MousePointer2 className="h-4 w-4" />,
                'Select',
                'Select / move / rotate (Shift+drag to rotate)'
              )}
              {toolButton(
                'path',
                <Route className="h-4 w-4" />,
                'Path',
                'Path editing — click the map to add waypoints to the selected aircraft'
              )}
              <ToolbarDivider />
              <ToolbarButton
                icon={<Plane className="h-4 w-4" />}
                label="Actors"
                accent="amber"
                active={inspectorOpen}
                onClick={() => setInspectorOpen((v) => !v)}
                title="Actor list and properties"
              />
              <FlythroughButton
                scope={flythroughScope}
                open={flythroughOpen}
                onToggle={() => setFlythroughOpen((v) => !v)}
                compact
              />
              <CinemaToggleButton compact />
            </div>

            {/* Left drawer */}
            {leftPanel !== 'none' && (
              <div className="absolute left-4 top-20">
                {leftPanel === 'scenes' ? (
                  <SceneManagerPanel
                    locationId={locationId ?? null}
                    locationSlug={locationSlug ?? null}
                    origin={center ?? null}
                    flythroughScope={flythroughScope}
                    onClose={() => setLeftPanel('none')}
                  />
                ) : (
                  <AircraftLibraryPanel
                    catalog={catalogQuery.data}
                    isLoading={catalogQuery.isLoading}
                    onClose={() => setLeftPanel('none')}
                  />
                )}
              </div>
            )}

            {/* Flythrough authoring */}
            {flythroughOpen && (
              <div className="absolute right-4 top-16 z-30">
                <FlythroughPanel
                  map={map}
                  scope={flythroughScope}
                  onClose={() => setFlythroughOpen(false)}
                />
              </div>
            )}

            {/* Right rail: actors */}
            {inspectorOpen && !flythroughOpen && (
              <ActorInspectorPanel onClose={() => setInspectorOpen(false)} />
            )}

            {/* Transport */}
            <StudioTimelineBar durationMs={durationMs} />
          </div>
        )}
      />

      {!sceneReady && (
        <div className="absolute inset-0 z-20 flex items-center justify-center bg-slate-950">
          <div className="space-y-3 text-center">
            <div className="mx-auto h-10 w-10 animate-spin rounded-full border-2 border-sky-500/30 border-t-sky-400" />
            <div className="text-sm text-white/60">Loading cinematic studio…</div>
          </div>
        </div>
      )}
    </div>
  )
}
